//----------------------------------------------------
// src/pages/LoginPage.jsx — Logboek24 Login (RBAC v2)
//----------------------------------------------------
import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import { useUser } from "../context/UserContext";
import { API_BASE_URL } from "../config/api";
import { isStrongAuthEnrollmentRequired } from "../utils/strongAuth";

export default function LoginPage() {
  const { login } = useUser();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [wachtwoord, setWachtwoord] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const API_URL = API_BASE_URL;

  function finalizeLoginFromToken(token, refreshToken = "") {
    if (!token) {
      throw new Error("Geen toegangstoken ontvangen van de server.");
    }

    const decoded = JSON.parse(atob(token.split(".")[1]));
    const tokenRoles = Array.isArray(decoded.roles) ? decoded.roles : [];
    const normalizedRoles = [
      ...tokenRoles.map((role) => String(role || "").trim().toLowerCase()),
      String(decoded.rol || "").trim().toLowerCase(),
      String(decoded.role || "").trim().toLowerCase(),
    ].filter(Boolean);
    const accountType = String(decoded.accountType || "internal").trim().toLowerCase();

    login(token, refreshToken);

    if (decoded.forcePasswordReset === true) {
      navigate("/wachtwoord-reset", { replace: true });
      return;
    }

    // The backend determines whether this session must enroll.  Keep the
    // legacy claim as a fallback while accepting the new nested contract.
    if (isStrongAuthEnrollmentRequired(decoded)) {
      navigate("/mfa/setup", { replace: true });
      return;
    }

    if (accountType === "customer_portal") {
      navigate("/portaal", { replace: true });
      return;
    }

    if (normalizedRoles.includes("platformadmin")) {
      navigate("/admin/dashboard", { replace: true });
      return;
    }

    if (normalizedRoles.includes("zzp_owner")) {
      navigate("/pl", { replace: true });
      return;
    }

    if (normalizedRoles.includes("monteur")) {
      navigate("/monteur/werkbonnen", { replace: true });
      return;
    }

    if (normalizedRoles.includes("tenantadmin")) {
      navigate("/pl", { replace: true });
      return;
    }

    if (normalizedRoles.includes("controller")) {
      navigate("/pl", { replace: true });
      return;
    }

    if (normalizedRoles.includes("projectleider")) {
      navigate("/pl", { replace: true });
      return;
    }

    navigate("/dashboard", { replace: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const endpoint = mfaRequired
        ? `${API_URL}/api/auth/login/mfa`
        : `${API_URL}/api/auth/login`;
      const payload = mfaRequired
        ? {
            mfaToken,
            code: mfaCode,
          }
        : {
            email,
            wachtwoord,
          };

      const res = await axios.post(endpoint, payload);

      if (res?.data?.mfaRequired === true && res?.data?.mfaToken) {
        setMfaRequired(true);
        setMfaToken(String(res.data.mfaToken || ""));
        setMfaCode("");
        setLoading(false);
        return;
      }

      finalizeLoginFromToken(
        res?.data?.accessToken,
        String(res?.data?.refreshToken || "").trim()
      );
    } catch (err) {
      console.error("❌ Login fout:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Inloggen mislukt. Controleer je gegevens."
      );
    } finally {
      setLoading(false);
    }
  }

  async function handlePasskeyLogin() {
    if (loading || passkeyLoading || mfaRequired) return;
    setError("");

    const normalizedEmail = String(email || "").trim();
    if (!normalizedEmail) {
      setError("Vul eerst je e-mailadres in voor passkey login.");
      return;
    }

    if (!window.PublicKeyCredential) {
      setError("Passkey wordt niet ondersteund in deze browser.");
      return;
    }

    try {
      setPasskeyLoading(true);
      const startRes = await axios.post(`${API_URL}/api/auth/login/passkey/start`, {
        email: normalizedEmail,
      });
      const options = startRes?.data?.options;
      const challengeToken = String(startRes?.data?.challengeToken || "").trim();
      if (!options || !challengeToken) {
        throw new Error("Passkey challenge ontvangen mislukt");
      }

      const authenticationResponse = await startAuthentication({ optionsJSON: options });
      const finishRes = await axios.post(`${API_URL}/api/auth/login/passkey/finish`, {
        challengeToken,
        response: authenticationResponse,
      });
      finalizeLoginFromToken(
        finishRes?.data?.accessToken,
        String(finishRes?.data?.refreshToken || "").trim()
      );
    } catch (err) {
      const isCancelled =
        err?.name === "AbortError" ||
        String(err?.message || "").toLowerCase().includes("cancel");
      if (!isCancelled) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Passkey login mislukt. Probeer opnieuw."
        );
      }
    } finally {
      setPasskeyLoading(false);
    }
  }

  return (
    <div
      className="min-h-[100dvh] bg-[#f3f4f6] px-4 py-8"
      style={{ paddingTop: "max(env(safe-area-inset-top), 2rem)" }}
    >
      <div className="mx-auto w-full max-w-xl rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
        <h1 className="mb-6 text-3xl font-semibold tracking-tight text-slate-900">Inloggen</h1>

        {error ? (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-3">
          {!mfaRequired ? (
            <>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-mailadres"
                className="h-12 w-full rounded-xl border border-slate-200 bg-[#f5f4dd] px-4 text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white"
                autoComplete="email webauthn"
                required
              />

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={wachtwoord}
                  onChange={(e) => setWachtwoord(e.target.value)}
                  placeholder="Wachtwoord"
                  className="h-12 w-full rounded-xl border border-slate-200 bg-[#f5f4dd] px-4 pr-20 text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white"
                  autoComplete="current-password webauthn"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 px-3 text-sm font-medium text-slate-500 hover:text-slate-800"
                  aria-label={showPassword ? "Verberg wachtwoord" : "Toon wachtwoord"}
                >
                  {showPassword ? "Verberg" : "Toon"}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-slate-700">
                Multi-factor verificatie vereist. Voer je code uit Microsoft Authenticator in.
              </p>
              <input
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="MFA code"
                className="h-12 w-full rounded-xl border border-slate-200 bg-[#f5f4dd] px-4 text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white"
                autoFocus
                required
              />
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-1 h-12 w-full rounded-xl bg-[#77aee4] text-base font-semibold text-white transition hover:bg-[#679fd8] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? "Bezig..." : mfaRequired ? "Code verifiëren" : "Inloggen"}
          </button>

          {!mfaRequired ? (
            <>
              <div className="my-2 flex items-center gap-3 py-1 text-xs text-slate-400">
                <div className="h-px flex-1 bg-slate-200" />
                <span>of</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <button
                type="button"
                disabled={loading || passkeyLoading}
                onClick={handlePasskeyLogin}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                {passkeyLoading ? "Passkey starten..." : "Inloggen met passkey"}
              </button>
              <Link
                to="/wachtwoord-vergeten"
                className="flex h-11 w-full items-center justify-center rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Wachtwoord vergeten?
              </Link>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setMfaRequired(false);
                setMfaToken("");
                setMfaCode("");
                setError("");
              }}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Terug naar login
            </button>
          )}
        </form>

        <p className="mt-6 text-center text-xs text-slate-400">
          Logboek24 © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
